import { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import { toast } from "../../lib/notices";
import { DataTable, Section } from "./AdminUI";
import { formatDateTime } from "../../lib/datetime";

const STATUS_CHIP = {
  pending: "brutal-chip",
  invited: "brutal-chip brutal-chip-warning",
  joined: "brutal-chip brutal-chip-success",
  rejected: "brutal-chip brutal-chip-danger",
};

export default function WaitlistPanel() {
  const [wl, setWl] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [waitlistMode, setWaitlistMode] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  // The backend returns a fresh invite link once and never again, so it is held
  // here for the operator to copy when mail delivery is not configured.
  const [inviteLinks, setInviteLinks] = useState({});

  const loadWaitlist = useCallback(async (status) => {
    try {
      const r = await api.get("/admin/waitlist", { params: status ? { status } : {} });
      setWl(r.data); setError("");
    } catch (e) {
      setError(errMsg(e, "Could not load the waitlist"));
    }
  }, []);

  useEffect(() => { loadWaitlist(filter); }, [filter, loadWaitlist]);
  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setWaitlistMode(!!r.data?.waitlist_mode))
      .catch(() => setWaitlistMode(null));
  }, []);

  const toggleMode = async () => {
    const next = !waitlistMode;
    setBusy("mode");
    try {
      await api.post("/admin/settings", { waitlist_mode: next });
      setWaitlistMode(next);
      toast.success(next ? "Waitlist mode on — signups are invite-only" : "Waitlist mode off — open signups");
    } catch (e) { toast.error(errMsg(e, "Could not change the setting")); }
    finally { setBusy(""); }
  };

  const grant = async (entry) => {
    setBusy(entry.waitlist_id);
    try {
      const r = await api.post(`/admin/waitlist/${entry.waitlist_id}/invite`, {});
      if (r.data?.email_sent) toast.success(`Invite emailed to ${entry.email}`);
      else {
        // Previously this told the operator to "copy the link from the row"
        // when no such link was ever rendered. Now it actually is.
        setInviteLinks((m) => ({ ...m, [entry.waitlist_id]: r.data.invite_url }));
        toast.warning("Invite created but email could not be sent — copy the link shown in the row.");
      }
      await loadWaitlist(filter);
    } catch (e) { toast.error(errMsg(e, "Could not grant access")); }
    finally { setBusy(""); }
  };

  const reject = async (entry) => {
    if (!window.confirm(`Reject ${entry.email}? Any live invite link is revoked.`)) return;
    setBusy(entry.waitlist_id);
    try {
      await api.post(`/admin/waitlist/${entry.waitlist_id}/reject`, {});
      await loadWaitlist(filter);
    } catch (e) { toast.error(errMsg(e, "Could not reject")); }
    finally { setBusy(""); }
  };

  return (
    <Section
      id="waitlist"
      title="// WAITLIST"
      actions={waitlistMode !== null && (
        <div className="flex items-center gap-3">
          <span className="font-mono-ui text-2xs uppercase tracking-widest text-ink-muted">
            Signups: {waitlistMode ? "invite-only" : "open to everyone"}
          </span>
          <button type="button" onClick={toggleMode} disabled={busy === "mode"} aria-pressed={waitlistMode}
                  className={`brutal-btn brutal-btn-sm ${waitlistMode ? "" : "brutal-btn-secondary"}`}
                  data-testid="toggle-waitlist-mode">
            {waitlistMode ? "Turn waitlist off" : "Turn waitlist on"}
          </button>
        </div>
      )}
    >
      {error && <div className="brutal-card p-6 text-ink-accent text-sm mb-4" role="alert">{error}</div>}

      <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Filter by status">
        {["pending", "invited", "joined", "rejected", ""].map((s) => (
          <button key={s || "all"} type="button" onClick={() => setFilter(s)} aria-pressed={filter === s}
                  className={`brutal-btn brutal-btn-sm ${filter === s ? "" : "brutal-btn-secondary"}`}>
            {s || "all"}
            {wl?.counts && s && <span className="ml-1 tabular-nums">({wl.counts[s] ?? 0})</span>}
          </button>
        ))}
      </div>

      <DataTable
        caption="Waitlist applicants, newest first"
        empty={`No ${filter || ""} entries yet.`}
        columns={[{ key: "email", label: "Email" }, { key: "name", label: "Name" },
                  { key: "use_case", label: "Use case" }, { key: "status", label: "Status" },
                  { key: "joined", label: "Joined" }, { key: "actions", label: "Actions", srOnly: true }]}
        rows={wl?.entries}
        renderRow={(e) => (
          <tr key={e.waitlist_id} className="border-t border-rule align-top">
            <td className="p-3 font-mono-ui text-xs break-all">
              {e.email}
              {inviteLinks[e.waitlist_id] && (
                <div className="mt-2">
                  <label className="sr-only" htmlFor={`invite-${e.waitlist_id}`}>Invite link for {e.email}</label>
                  <input id={`invite-${e.waitlist_id}`} readOnly className="brutal-input text-xs w-full"
                         value={inviteLinks[e.waitlist_id]}
                         onFocus={(ev) => ev.target.select()}
                         data-testid={`invite-link-${e.waitlist_id}`} />
                </div>
              )}
            </td>
            <td className="p-3">{e.name || <span className="text-ink-subtle">—</span>}</td>
            <td className="p-3 max-w-xs text-ink-muted">{e.use_case || <span className="text-ink-subtle">—</span>}</td>
            <td className="p-3"><span className={STATUS_CHIP[e.status] || "brutal-chip"}>{e.status}</span></td>
            <td className="p-3 font-mono-ui text-xs text-ink-muted">{formatDateTime(e.created_at)}</td>
            <td className="p-3">
              <div className="flex gap-2 justify-end">
                {e.status !== "joined" && (
                  <button type="button" onClick={() => grant(e)} disabled={busy === e.waitlist_id}
                          className="brutal-btn brutal-btn-sm" aria-label={`Grant access to ${e.email}`}
                          data-testid={`grant-${e.waitlist_id}`}>
                    <Check size={12} aria-hidden="true" />
                    {e.status === "invited" ? "Re-invite" : "Grant"}
                  </button>
                )}
                {e.status !== "joined" && e.status !== "rejected" && (
                  <button type="button" onClick={() => reject(e)} disabled={busy === e.waitlist_id}
                          className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                          aria-label={`Reject ${e.email}`}>
                    <X size={12} aria-hidden="true" />
                  </button>
                )}
              </div>
            </td>
          </tr>
        )}
      />
    </Section>
  );
}
